import {useTranslation} from '@aboutyou/i18n';
import {
  FilterCard,
  FilterFlyout,
  FilterFlyoutBoolean,
  FilterFlyoutFooter,
  FilterFlyoutMultipleBoolean,
  GenericIconName,
  Icon,
} from '@aboutyou/ui-components';
import React from 'react';
import styled from 'styled-components';
import {
  YourDealsInterface,
  useYourDealsSection,
} from '../../shared/useYourDealsSection';

type Props = {
  hide: VoidFunction;
  isOpen: boolean;
  open: (sections: string[]) => void;
  submit: VoidFunction;
  enableFlyoutFilter: boolean;
};

const FlyoutFilter = (
  props: YourDealsInterface &
    Props & {isSaleFilterActive: boolean; open: VoidFunction},
) => {
  const {
    open,
    isOpen,
    isSaleFilterActive,
    data,
    submit,
    resetFilter,
    setFilter,
    hide,
    enableFlyoutFilter,
  } = props;
  const {t} = useTranslation();

  return (
    <div data-testid="filterFlyoutSectionYourDeals">
      <FilterCard
        disableHorizontalPadding
        onClick={open}
        title={
          enableFlyoutFilter ? (
            <SaleHeaderWrapper>
              <Icon asset={GenericIconName.SaleEvent} size="XS" />
              {t('Sale')}
            </SaleHeaderWrapper>
          ) : (
            t('Your deals')
          )
        }
      >
        <FilterFlyout
          data-testid="filterFlyoutSectionYourDeals_filterFlyout"
          onClose={hide}
          open={isOpen}
          title={enableFlyoutFilter ? t('Sale') : t('Your deals')}
          primaryButtonLabel={t('Submit')}
          secondaryButtonLabel={t('Back')}
          footer={FilterFlyoutFooter}
          onPrimaryButtonClick={submit}
        >
          <FilterFlyoutBoolean
            active={isSaleFilterActive}
            data-testid="filterFlyoutSaleToggle"
            label={data.sale.headline}
            sublabel={enableFlyoutFilter ? data.sale.subline : undefined}
            onToggle={() =>
              isSaleFilterActive
                ? resetFilter(data.sale.slug)
                : setFilter({
                    filterId: 'true',
                    filterSection: data.sale.slug,
                  })
            }
          />
        </FilterFlyout>
      </FilterCard>
    </div>
  );
};

const StandardFilter = (
  props: YourDealsInterface & Props & {isSaleFilterActive: boolean},
) => {
  const {isSaleFilterActive, data, resetFilter, setFilter} = props;
  return (
    <FilterCard>
      <FilterFlyoutBoolean
        active={isSaleFilterActive}
        data-testid="filterFlyoutSaleToggle"
        label={data.sale.headline}
        onToggle={() =>
          isSaleFilterActive
            ? resetFilter(data.sale.slug)
            : setFilter({
                filterId: 'true',
                filterSection: data.sale.slug,
              })
        }
      />
    </FilterCard>
  );
};

export const FilterSectionYourDeals: React.FC<
  YourDealsInterface & Props
> = props => {
  const {
    activeFilters,
    data,
    resetFilter,
    resetFilters,
    setFilters,
    enableFlyoutFilter,
    isOpen,
    hide,
    open,
  } = props;
  const {
    booleanFilters,
    onToggleFilterContent,
    isSaleFilterActive,
    filterSlug,
  } = useYourDealsSection({
    activeFilters,
    data,
    resetFilter,
    resetFilters,
    setFilters,
  });

  const handleOpen = () => {
    open([filterSlug]);
  };

  if (!data.discount) {
    return enableFlyoutFilter ? (
      <FlyoutFilter
        {...props}
        open={handleOpen}
        isSaleFilterActive={isSaleFilterActive}
      />
    ) : (
      <StandardFilter {...props} isSaleFilterActive={isSaleFilterActive} />
    );
  }

  return (
    <FilterFlyoutMultipleBoolean
      label={data.label}
      onToggleFilterContent={onToggleFilterContent}
      booleanFilters={booleanFilters}
      hide={hide}
      isOpen={isOpen}
      open={handleOpen}
      enableFlyoutFilter={enableFlyoutFilter}
    />
  );
};

const SaleHeaderWrapper = styled.div`
  display: flex;
  gap: 5px;
`;
